import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useNavigate } from "react-router-dom";
import { clientSearchesApi } from "core/api";
import {
  formatClientSearchOwner,
  parseClientSearchError,
} from "core/api/client-searches/client-searches.helpers";
import {
  ClientSearchOwner,
  ClientSearchRow,
} from "core/types/client-search.types";
import {
  NO_ESCAPE,
  SuspendedChip,
  fmtSearchDate,
  isOwnerSuspended,
} from "./client-searches.utils";

// I filtri sono lato client, quindi servono TUTTE le ricerche e non la prima pagina da
// 200 del default: si chiede il massimo consentito (1000) e si prosegue finche' una
// pagina torna corta. Il tetto e lo stop su "nessuna riga nuova" evitano un ciclo senza
// fine se il backend ignorasse offset.
const PAGE_SIZE = 1000;
const MAX_PAGES = 10;

// Oltre questo numero di parole chiave la cella diventa un muro di chip: il resto va
// nel tooltip del "+N".
const MAX_PARAM_CHIPS = 4;

const ownerHaystack = (o: ClientSearchOwner): string =>
  [o.firstName, o.lastName, o.email, o.id].filter(Boolean).join(" ");

/** Ricerche di TUTTI i clienti (anche quelle create da RM). Click → dettaglio ricerca. */
export const ClientSearchesListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ClientSearchRow[]>([]);
  const [ownersResolved, setOwnersResolved] = useState(true);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const seen = new Set<number>();
        const all: ClientSearchRow[] = [];
        let resolved = true;
        let complete = false;
        for (let page = 0; page < MAX_PAGES; page++) {
          const res = await clientSearchesApi.list({
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
          });
          resolved = resolved && res.ownersResolved;
          // Fra una pagina e l'altra RM puo' creare una ricerca: con created_at DESC
          // tutto scala di una riga e la stessa ricerca ricomparirebbe due volte.
          let added = 0;
          for (const s of res.searches) {
            if (!seen.has(s.searchId)) {
              seen.add(s.searchId);
              all.push(s);
              added++;
            }
          }
          if (res.searches.length < PAGE_SIZE) {
            complete = true;
            break;
          }
          // Pagina piena ma tutta gia' vista: offset ignorato. Ci si ferma e l'elenco
          // resta segnalato come incompleto.
          if (added === 0) break;
        }
        if (active) {
          setRows(all);
          setOwnersResolved(resolved);
          setTruncated(!complete);
        }
      } catch (e: any) {
        if (active) {
          setError(parseClientSearchError(e, e?.message || String(e)).message);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Proprietari unici per la select, in ordine alfabetico del nome mostrato.
  const owners = useMemo(() => {
    const byId = new Map<string, ClientSearchOwner>();
    rows.forEach((r) => {
      if (r.owner.id && !byId.has(r.owner.id)) byId.set(r.owner.id, r.owner);
    });
    return Array.from(byId.values()).sort((a, b) =>
      formatClientSearchOwner(a).localeCompare(formatClientSearchOwner(b))
    );
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (ownerFilter && r.owner.id !== ownerFilter) return false;
      if (!q) return true;
      const hay = [r.fullName, ownerHaystack(r.owner)].join(" ").toLowerCase();
      return hay.indexOf(q) !== -1;
    });
  }, [rows, query, ownerFilter]);

  const isFiltered = !!query.trim() || !!ownerFilter;

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      {/* Intestazione */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <PersonSearchOutlinedIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          {t("clientSearches.list.title")}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("clientSearches.list.description")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {t("clientSearches.list.error_load", { error, interpolation: NO_ESCAPE })}
        </Alert>
      )}

      {/* Keycloak non leggibile: la lista esce lo stesso, ma al posto del nome del
          cliente c'e' solo l'id dell'account. Meglio dirlo che lasciar pensare a un bug. */}
      {!ownersResolved && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("clientSearches.list.owners_unresolved")}
        </Alert>
      )}

      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("clientSearches.list.truncated", { count: rows.length })}
        </Alert>
      )}

      <Paper sx={{ borderRadius: "10px", padding: "20px", mb: 2 }}>
        <Box
          paddingY={"15px"}
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          flexWrap={"wrap"}
          gap={2}
        >
          <Typography fontSize={"16px"} fontWeight={700}>
            {isFiltered
              ? t("clientSearches.list.count_filtered", {
                  shown: filtered.length,
                  total: rows.length,
                })
              : t("clientSearches.list.count", { count: rows.length })}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="client-searches-owner-filter">
                {t("clientSearches.list.owner_filter")}
              </InputLabel>
              <Select
                labelId="client-searches-owner-filter"
                label={t("clientSearches.list.owner_filter")}
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(String(e.target.value))}
              >
                <MenuItem value="">{t("clientSearches.list.owner_all")}</MenuItem>
                {owners.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {formatClientSearchOwner(o)}
                    {o.email && formatClientSearchOwner(o) !== o.email
                      ? ` — ${o.email}`
                      : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder={t("clientSearches.list.search_placeholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Box>

        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                padding: "40px 0",
              }}
            >
              <CircularProgress color="secondary" size={"80px"} />
            </div>
          ) : (
            <TableContainer
              component={Paper}
              style={{
                position: "relative",
                borderRadius: "8px",
                maxHeight: "640px",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("clientSearches.list.table.date")}</TableCell>
                    <TableCell>{t("clientSearches.list.table.client")}</TableCell>
                    <TableCell>{t("clientSearches.list.table.person")}</TableCell>
                    <TableCell>{t("clientSearches.list.table.keywords")}</TableCell>
                    <TableCell align="right">
                      {t("clientSearches.list.table.results")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    "& .MuiTableCell-root": {
                      padding: "10px",
                      fontSize: "14px",
                    },
                  }}
                >
                  {filtered.map((s) => {
                    const ownerName = formatClientSearchOwner(s.owner);
                    const shownParams = s.params.slice(0, MAX_PARAM_CHIPS);
                    const hiddenParams = s.params.slice(MAX_PARAM_CHIPS);
                    return (
                      <TableRow
                        key={s.searchId}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate("/web-rep/client-searches/" + s.searchId)
                        }
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {fmtSearchDate(s.searchCreationTime, i18n.language)}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontSize="14px" fontWeight={600}>
                                {ownerName || "—"}
                              </Typography>
                              {s.owner.email && s.owner.email !== ownerName && (
                                <Typography fontSize="12px" color="text.secondary">
                                  {s.owner.email}
                                </Typography>
                              )}
                            </Box>
                            {isOwnerSuspended(s.owner) && <SuspendedChip />}
                          </Stack>
                        </TableCell>
                        <TableCell>{s.fullName || "—"}</TableCell>
                        <TableCell>
                          {s.params.length ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {shownParams.map((p) => (
                                <Chip key={p} size="small" label={p} />
                              ))}
                              {hiddenParams.length > 0 && (
                                <Tooltip title={hiddenParams.join(", ")}>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`+${hiddenParams.length}`}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {s.totResults === null ? "—" : s.totResults}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!filtered.length && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography
                          color="text.secondary"
                          sx={{ py: 3, textAlign: "center" }}
                        >
                          {t("clientSearches.list.empty")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </Paper>
    </Box>
  );
};

export default ClientSearchesListPage;
